import type { SymbolNotesService } from '.';
import { signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { MutationObserver } from '@tanstack/query-core';
import type { QueryClient } from '@tanstack/query-core';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { inject, injectable } from 'src/features/ioc';
import { getAllSymbolNotesQuery, getSaveSymbolNoteMutation } from 'src/queries/symbolNotes';

/** Debounce delay for saving notes (1 second) */
const SAVE_DEBOUNCE_MS = 1000;

@injectable()
export class SymbolNotesServiceImpl implements SymbolNotesService {
    #notes: Signal<Map<string, string>> = signal(new Map());
    #initialized: Signal<boolean> = signal(false);
    #pendingSaves: Map<string, ReturnType<typeof setTimeout>> = new Map();
    #log: Logger;
    #queryClient: QueryClient;
    #saveNoteMutation: MutationObserver<void, Error, { symbol: string; note: string }>;

    constructor(@inject('QueryClient') queryClient: QueryClient, @inject('Logger') logger: Logger) {
        this.#queryClient = queryClient;
        this.#log = logger.getSubLogger({ name: 'SymbolNotesService' });
        this.#saveNoteMutation = new MutationObserver(queryClient, getSaveSymbolNoteMutation());
    }

    get notes(): ReadonlySignal<Map<string, string>> {
        return this.#notes;
    }

    get initialized(): ReadonlySignal<boolean> {
        return this.#initialized;
    }

    async initialize(): Promise<void> {
        if (this.#initialized.value) return;

        try {
            const notesMap = await this.#queryClient.fetchQuery(getAllSymbolNotesQuery());
            this.#notes.value = notesMap;
            this.#initialized.value = true;
            this.#log.debug('SymbolNotesService initialized', { noteCount: notesMap.size });
        } catch (error) {
            this.#log.error('Failed to initialize SymbolNotesService', { error });
            this.#notes.value = new Map();
            this.#initialized.value = true;
        }
    }

    getNoteForSymbol(symbol: string): string | undefined {
        return this.#notes.value.get(symbol);
    }

    updateNote(symbol: string, note: string): void {
        // Update local state immediately
        const currentNotes = new Map(this.#notes.value);
        if (note.trim() === '') {
            currentNotes.delete(symbol);
        } else {
            currentNotes.set(symbol, note);
        }
        this.#notes.value = currentNotes;

        // Cancel any pending save for this symbol
        const existingTimeout = this.#pendingSaves.get(symbol);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
        }

        // Schedule debounced save
        const timeoutId = setTimeout(() => {
            this.#saveNoteToBackend(symbol, note);
            this.#pendingSaves.delete(symbol);
        }, SAVE_DEBOUNCE_MS);

        this.#pendingSaves.set(symbol, timeoutId);
    }

    #saveNoteToBackend(symbol: string, note: string): void {
        this.#saveNoteMutation.mutate(
            { symbol, note },
            {
                onSuccess: () => {
                    this.#log.debug(note.trim() === '' ? 'Deleted note for symbol' : 'Saved note for symbol', {
                        symbol,
                    });
                },
                onError: (error) => {
                    this.#log.error('Failed to save note to backend', { symbol, error });
                },
            },
        );
    }
}
